import {
  SimulationWrapperType,
  type SimulationWrapperConfig,
} from "@/test-set/simulation-wrappers"

import type { TestCaseSimulationMetadataInput } from "@/types/backend-contracts"
import { isJsonObjectValue, type JsonFieldValue } from "@/types/json"

import type { EditableTestCase, EditableTestCaseRecordLike } from "../model"

function asSimulationWrapperConfig(
  value: JsonFieldValue | undefined,
): SimulationWrapperConfig | null {
  if (!isJsonObjectValue(value)) {
    return null
  }

  const preSimulation = value.pre_simulation
  const postSimulation = value.post_simulation
  if (
    (preSimulation !== undefined && !Array.isArray(preSimulation)) ||
    (postSimulation !== undefined && !Array.isArray(postSimulation))
  ) {
    return null
  }

  return value as unknown as SimulationWrapperConfig
}

export function getTestSetSimulationWrapperConfig(
  wrapperType: SimulationWrapperType | null | undefined,
  testCases: Pick<EditableTestCaseRecordLike, "simulation_metadata_input">[],
): SimulationWrapperConfig | null {
  if (wrapperType !== SimulationWrapperType.API_WRAPPER) {
    return null
  }

  for (const testCase of testCases) {
    const config = asSimulationWrapperConfig(
      testCase.simulation_metadata_input?.[wrapperType],
    )
    if (config) {
      return config
    }
  }

  return null
}

export function applyTestSetSimulationWrapperConfig({
  config,
  onlyApplyToMissingApiConfig = false,
  testCases,
  wrapperType,
}: {
  config: SimulationWrapperConfig | null | undefined
  onlyApplyToMissingApiConfig?: boolean
  testCases: EditableTestCase[]
  wrapperType: SimulationWrapperType | null | undefined
}): EditableTestCase[] {
  const hasApiCall = Boolean(config?.pre_simulation?.length)

  return testCases.map((testCase) => {
    const simulationMetadata: TestCaseSimulationMetadataInput = {
      ...(testCase.simulation_metadata_input ?? {}),
    }

    if (
      onlyApplyToMissingApiConfig &&
      simulationMetadata[SimulationWrapperType.API_WRAPPER] !== undefined
    ) {
      return testCase
    }

    delete simulationMetadata[SimulationWrapperType.API_WRAPPER]

    if (wrapperType === SimulationWrapperType.API_WRAPPER && hasApiCall) {
      simulationMetadata[SimulationWrapperType.API_WRAPPER] =
        config as unknown as JsonFieldValue
    }

    return {
      ...testCase,
      simulation_metadata_input:
        Object.keys(simulationMetadata).length > 0 ? simulationMetadata : null,
    }
  })
}
